import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useReduxSelector } from "@hooks/useReduxSelector";
import { Box as RawBox } from "@material-ui/core";
import { ThemeProvider, withStyles } from "@material-ui/core/styles";
import { createTheme } from "@material-ui/core/styles";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import { Rating } from "@material-ui/lab";

import { InputGroup, ListboxSimple } from "@components/tailwindComponents";

import {
  IndividualObjectiveScoreType,
  REGEX_NOT_NUMBER,
} from "@helpers/_constants";
import { IndividualObjective } from "@services/developmentPlan";

import { MAX_BY_SCORE_TYPE, useScoreMapper } from "./useNotationMapper";

/**
 * @type {(props: import("@material-ui/core").BoxProps) => JSX.Element}
 */
// @ts-ignore
const Box = RawBox;

/**
 * @type {typeof Rating}
 */
// @ts-ignore
const StyledRating = withStyles({
  iconFilled: {
    color: "#81dac9",
  },
  iconHover: {
    color: "#73C7B7",
  },
  label: {
    marginBottom: "0",
  },
})(Rating);

const arhTheme = createTheme({
  palette: {
    secondary: {
      main: "#73C7B7",
    },
    primary: {
      main: "#73C7B7",
    },
  },
});

const SCORE_TYPE_LIST = Object.values(IndividualObjectiveScoreType).map(
  (value) => ({ _id: value, name: value }),
);

export default function IndividualObjectiveScore(props) {
  const { t: translate } = useTranslation("assesment.frequency");

  const { individualObjective, readOnly } = props;
  const isNotApplicable =
    individualObjective.scoreType === IndividualObjectiveScoreType.NOT_APPLICABLE;

  // Score Type Management
  const is3StarsRating = useMemo(() => {
    return (
      individualObjective.scoreType ===
      IndividualObjectiveScoreType.RATING_3_STARS
    );
  }, [individualObjective.scoreType]);

  const enterpriseId = useReduxSelector(
    ({ settings }) => settings.enterprise._id,
  );

  async function handleScoreTypeChange(scoreType) {
    await IndividualObjective.updateById({
      _id: individualObjective._id,
      scoreType: scoreType._id,
      Enterprise: enterpriseId,
    });
  }

  // Score Management
  const { inputValue, mapScoreToPercent } = useScoreMapper({
    scoreType: individualObjective.scoreType,
    score: individualObjective.score,
  });

  async function handleScoreChange(score) {
    await IndividualObjective.updateById({
      _id: individualObjective._id,
      score: mapScoreToPercent(score),
      Enterprise: enterpriseId,
    });
  }

  return (
    <div className="arh-relative">
      {!readOnly && (
        <ListboxSimple
          placeholder={isNotApplicable ? translate("N/A") : "0"}
          name="score"
          optionList={SCORE_TYPE_LIST}
          selectedValue={null}
          setSelectedValue={handleScoreTypeChange}
          noRadio
          invalid={false}
          readOnly={false}
          customClass="!arh-ml-0"
          anotherParentClass="desktop-middle:arh-w-[160%] arh-right-0"
          withTooltip
          disabled={false}
          customTranslator={translate}
        />
      )}

      {isNotApplicable ? (
        readOnly && (
          <span className="arh-block arh-text-center">{translate("N/A")}</span>
        )
      ) : is3StarsRating ? (
        <ThreeStarsRating
          value={inputValue}
          onChange={handleScoreChange}
          name={`rating-stars-${individualObjective._id}`}
          readOnly={readOnly}
        />
      ) : (
        <NormalRating
          scoreType={individualObjective.scoreType}
          value={inputValue}
          onChange={handleScoreChange}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

function ThreeStarsRating(props) {
  return (
    <div
      className={`${!props.readOnly ? "arh-absolute arh-left-1 arh-top-1/2 arh-z-[2] -arh-translate-y-1/2 arh-bg-white arh-pl-3 desktop-l:arh-w-2/3" : "arh-w-full arh-bg-transparent"}`}
    >
      <ThemeProvider theme={arhTheme}>
        <Box
          component="fieldset"
          borderColor="transparent"
          sx={{
            marginBottom: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <StyledRating
            name={props.name}
            value={props.value}
            max={3}
            precision={1}
            emptyIcon={<StarBorderIcon fontSize="inherit" />}
            onChange={(_, value) => {
              props.onChange(value);
            }}
            readOnly={props.readOnly}
          />
        </Box>
      </ThemeProvider>
    </div>
  );
}

function NormalRating(props) {
  const maxScore = useMemo(() => {
    return MAX_BY_SCORE_TYPE[props.scoreType];
  }, [props.scoreType]);

  const [inputValue, setInputValue] = useState("0");

  const formatValue = useCallback(
    (value) => {
      let nextState = value.replace(REGEX_NOT_NUMBER, "");

      if (nextState.match(/\./g)?.length > 1) {
        nextState = parseFloat(nextState).toString();
      }

      if (+nextState > maxScore) {
        return maxScore;
      }

      if (+nextState < 0) {
        return 0;
      }

      return nextState;
    },
    [maxScore],
  );

  useEffect(() => {
    setInputValue(props.value?.toString() || "0");
  }, [props.value]);

  function handleChange(event) {
    setInputValue(formatValue(event.target.value));
  }

  function handleBlur() {
    props.onChange(+inputValue);
  }

  return (
    <div
      className={`${
        !props.readOnly
          ? "arh-absolute arh-top-0 arh-z-[2] arh-w-4/5"
          : "arh-w-4/5 desktop-l:arh-w-full"
      }`}
    >
      <InputGroup
        name="note"
        content={`/${maxScore}`}
        classContent={`${!props.readOnly && "arh-border-r-0 arh-rounded-none"} arh-w-12 desktop-l:arh-w-1/2 arh-text-[#94ABB7] arh-h-[44px]`}
        classParent="arh-w-full"
        classInput="arh-h-[44px] !arh-px-3 desktop-l:!arh-px-2 arh-text-center"
        onBlur={handleBlur}
        onChange={handleChange}
        value={inputValue}
        disabled={props.readOnly}
        useToolTip={false}
        invalid={false}
      />
    </div>
  );
}
